import { useContext, useState } from 'react';
import { type OneDriveFile, type OneDriveFolder } from '../../types';
import SelectedFilesContext from '../../../SelectedFilesContext';
import ClickableItemTableRow from '../ClickableItemTableRow';
import { Checkbox } from '@mui/material';
import { FolderTwoTone as FolderIcon } from '@mui/icons-material';
import FileIconTableCell from '../utils/FileIconTableCell';
import LastModifiedTableCell from '../utils/LastModifiedTableCell';
import SecondaryTableCell from '../utils/SecondaryTableCell';
import FolderNameCell from './FolderNameCell';
import FolderLoadingIndicator from './FolderLoadingIndicator';
import FolderLoadErrorIcon from './FolderLoadErrorIcon';
import useIsAlreadyAttached from '../utils/useIsAlreadyAttached';
import * as Sentry from '@sentry/react';
import useLoadFolderItems from './useLoadFolderItems';
import { ExternalFileSource } from '@/lib/types';
import { FormattedMessage } from 'react-intl';

type OneDriveFolderTableRowProps = {
  folder: OneDriveFolder;
  onOpen: (
    folderName: string,
    folderItems: (OneDriveFile | OneDriveFolder)[],
  ) => void;
};

const OneDriveFolderTableRow = ({
  folder,
  onOpen,
}: OneDriveFolderTableRowProps) => {
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [folderItems, setFolderItems] = useState<
    (OneDriveFile | OneDriveFolder)[] | null
  >(null);

  const loadFolderItems = useLoadFolderItems();

  const { selectedFiles, selectFile, deselectFile, canSelectMoreFiles } =
    useContext(SelectedFilesContext);

  const folderUrl = folder.webUrl || null;
  const isSelected = folderUrl !== null && selectedFiles.has(folderUrl);

  const isAlreadyAttached = useIsAlreadyAttached(folder);

  const getFolderItems = async (): Promise<
    (OneDriveFile | OneDriveFolder)[] | null
  > => {
    if (folderItems !== null) {
      return folderItems;
    }

    setLoading(true);

    try {
      const folderItems = await loadFolderItems(folder);

      setFolderItems(folderItems);
      setLoading(false);

      return folderItems;
    } catch (error) {
      Sentry.captureException(error);

      setHasError(true);
      setLoading(false);

      return null;
    }
  };

  const handleOpen = async (): Promise<void> => {
    const folderItems = await getFolderItems();

    if (folderItems === null) {
      return;
    }

    onOpen(folder.name, folderItems);
  };

  const handleSelectToggle = (): void => {
    if (folderUrl === null) {
      return;
    }

    if (isSelected) {
      deselectFile(folderUrl);

      return;
    }

    selectFile({
      source: ExternalFileSource.MICROSOFT_ONEDRIVE,
      externalId: folder.id,
      filename: folder.name,
      url: folderUrl,
      mimeType: undefined,
    });
  };

  const itemCount = folder.folder.childCount;

  return (
    <>
      <ClickableItemTableRow
        selected={isSelected || isAlreadyAttached}
        onClick={handleSelectToggle}
        disabled={isAlreadyAttached || (!isSelected && !canSelectMoreFiles)}
      >
        <td>
          <Checkbox
            checked={isSelected || isAlreadyAttached}
            onChange={handleSelectToggle}
            disabled={
              folderUrl === null ||
              isAlreadyAttached ||
              (!isSelected && !canSelectMoreFiles)
            }
          />
        </td>
        <FileIconTableCell>
          {loading ? (
            <FolderLoadingIndicator />
          ) : hasError ? (
            <FolderLoadErrorIcon />
          ) : (
            <FolderIcon />
          )}
        </FileIconTableCell>
        <FolderNameCell
          folder={folder}
          onOpenFolder={handleOpen}
          loading={loading}
        />
        <LastModifiedTableCell oneDriveItem={folder} />
        <SecondaryTableCell>
          {itemCount === 0 && (
            <FormattedMessage id="portfolio.challenge.files.add.select.onedrive.file_picker.filesystem.folder.items.no_items" />
          )}
          {itemCount === 1 && (
            <FormattedMessage id="portfolio.challenge.files.add.select.onedrive.file_picker.filesystem.folder.items.one_item" />
          )}
          {itemCount > 1 && (
            <FormattedMessage
              id="portfolio.challenge.files.add.select.onedrive.file_picker.filesystem.folder.items.multiple_items"
              values={{
                itemCount,
              }}
            />
          )}
        </SecondaryTableCell>
      </ClickableItemTableRow>
    </>
  );
};

export default OneDriveFolderTableRow;
